import { useParams, Link } from 'react-router-dom';
import { BOARDS } from '@/data/content';
import Breadcrumb from '@/components/ui/Breadcrumb';

const colorText: Record<string, string> = { 'board-a': 'text-board-a', 'board-b': 'text-board-b', 'board-c': 'text-board-c', 'board-s': 'text-board-s' };
const colorBg: Record<string, string> = { 'board-a': 'bg-board-a/8', 'board-b': 'bg-board-b/8', 'board-c': 'bg-board-c/8', 'board-s': 'bg-board-s/8' };

const detailSections: Record<string, { title: string; items: string[] }[]> = {
  c: [
    { title: '传感器兼容', items: ['TCS34725 颜色传感器 (I2C)', 'HC-SR04P 超声波 (GPIO+Timer)', 'TCRT5000 红外避障 x2 (GPIO)'] },
    { title: '编程能力', items: ['图形化编程编程', '事件触发（按键→冲刺）', '条件判断（if-else 逻辑）', '参数修改（快慢/时长）', 'IMU 数据读取', 'GPIO 开关量控制'] },
    { title: '适用赛事', items: ['少年组对抗赛（标准动力，仅碰撞推挤）', '方案 1 自由对战'] },
  ],
  b: [
    { title: '传感器兼容', items: ['全部 C 板兼容传感器', 'OV2640 摄像头（基础视觉）', '更多 I²C/UART 设备并行'] },
    { title: '编程能力', items: ['Python 编程', 'PID 控制算法', '传感器驱动开发', 'Wi-Fi/BLE 无线通信', '基础计算机视觉（OpenCV）', '电脑端 Python/UDP 接管控制（可选）'] },
    { title: '扩展视觉', items: ['OV2640 摄像头（板载 FPC 接口）', '单轴/双轴舵机云台（GPIO PWM）', '云台支架（壳体顶部 12 孔位安装）'] },
    { title: '适用赛事', items: ['青少组对抗赛（可换电机/齿比，推铲类武器）', '竞速赛（全地形改装）', '编程挑战赛'] },
  ],
  a: [
    { title: '传感器兼容', items: ['全部 B 板兼容传感器', 'OV5640 MIPI 摄像头（高清视觉）', '激光雷达（SLAM 入门）', '电子罗盘（磁力计定向）'] },
    { title: '编程能力', items: ['Python / C++ 双语言', 'OpenCV 计算机视觉', '行为树 AI 策略', '有限状态机（FSM）', '传感器融合（卡尔曼滤波）', 'CAN 总线通信'] },
    { title: '适用赛事', items: ['菁英组对抗赛（全部合规武器）', '自主对抗编程赛', '工程创意赛'] },
  ],
  s: [
    { title: '传感器兼容', items: ['全部 A 板兼容传感器', '双目摄像头（深度视觉）', '高端 IMU（BMI088+IST8310）', '无限扩展——12个全功能口'] },
    { title: '编程能力', items: ['Linux (Debian) + ROS', 'C++ / Rust / RTOS', 'SLAM 同步定位与建图', '强化学习训练', '多线程实时控制', 'Docker 容器化部署'] },
    { title: '适用赛事', items: ['极客组对抗赛（含自研武器结构、无刷电机、高压电池）', '全部赛事类型', 'S 板用户不依赖基础版底盘——从零设计整车'] },
  ],
};

export default function BoardDetailPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const id = boardId?.toLowerCase() || 'a';
  const board = BOARDS.find((b) => b.id.toLowerCase() === id);
  const sections = detailSections[id] || detailSections.a;

  if (!board) return <div className="max-w-4xl mx-auto px-6 pt-8"><p className="text-text-disabled">主板未找到</p></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 pt-8 pb-16">
      <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '产品', href: '/products' }, { label: `${board.id} 板` }]} />

      <div className="flex flex-col md:flex-row items-start gap-8 mb-12">
        <div className={`w-full md:w-64 h-56 rounded-card-lg mecha-card mecha-bolt flex items-center justify-center shrink-0 ${colorBg[board.color]}`}>
          <img src={board.image} alt={board.name} className="h-48 w-auto object-contain" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-2xl font-extrabold ${colorText[board.color]}`}>{board.id} 板</span>
            <span className="text-sm text-text-disabled">{board.ageGroup}</span>
          </div>
          <p className="text-text-secondary text-sm mb-4">{board.subtitle}</p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm mb-5">
            {board.specs.map((s) => (
              <div key={s.label} className="flex justify-between"><span className="text-text-disabled">{s.label}</span><span className="text-text-secondary">{s.value}</span></div>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl font-extrabold text-text-primary">{board.price}</span>
            <span className="text-sm text-text-disabled">{board.programmingLang}</span>
            <Link to="/shop" className="ml-auto px-5 h-9 rounded-btn bg-arena-red text-white text-sm font-bold hover:brightness-110 transition-all inline-flex items-center">购买</Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {sections.map((sec) => (
          <div key={sec.title} className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-5">
            <h2 className="text-sm font-extrabold text-text-primary mb-4">{sec.title}</h2>
            <ul className="space-y-2">
              {sec.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-text-secondary">
                  <span className="w-1 h-1 rounded-full bg-arena-blue/60 mt-1.5 shrink-0" />{item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
